import { useCallback, useEffect, useRef, useState } from 'react';
import { Paper, Typography } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import { getSecurityHubConfig } from '../../../services/securityhubApi';
import SecurityHubSetup from './SecurityHubSetup';
import ComplianceDashboard from './ComplianceDashboard';
import OperationProgress from './OperationProgress';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalErrorState } from '../../../components/portal/PortalStates';
import { POLL_INTERVAL_MS } from './pollInterval';
import { CK, FONT_BODY, sectionCardSx, sectionHeadingSx } from '../portalStyles';

// SecurityHubEntry decides what to render at /securityhub. Flow:
//   no config / disabled / failed  → setup wizard
//   in_progress                    → transitioning view (polls until done)
//   enabled / partial_failure      → compliance dashboard
//
// The "in_progress" branch handles both initial enable (after the wizard
// submits) AND teardown initiated from Settings → Danger Zone, which
// navigates here so this component's polling drives the UI forward.
export default function SecurityHubEntry() {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const refresh = useCallback(async ({ showSpinner = true } = {}) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await getSecurityHubConfig();
      setConfig(res?.data || null);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll during any in_progress transition (enable or disable) so the view
  // advances automatically once the orchestrator goroutine finishes.
  useEffect(() => {
    if (config?.status !== 'in_progress') return undefined;
    pollRef.current = setInterval(() => refresh({ showSpinner: false }), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [config?.status, refresh]);

  // Keep a page header mounted in every state so the shell never disappears
  // between a fetch, an error and the resolved view.
  const header = (subtitle) => (
    <motion.div variants={sectionVariants}>
      <PortalPageHeader
        icon={<SecurityIcon sx={{ fontSize: 24 }} />}
        title="Security Hub"
        subtitle={subtitle}
      />
    </motion.div>
  );

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        {header('Checking your Security Hub configuration')}
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading Security Hub…" />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        {header('Could not load the Security Hub configuration')}
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => refresh()} />
        </motion.div>
      </motion.div>
    );
  }

  if (config?.status === 'in_progress') {
    const hint = (config.errorMessage || '').toLowerCase();
    let title = 'Security Hub setup in progress…';
    let body = 'Enrolling member accounts, associating standards, and creating the finding aggregator. This can take a few minutes.';
    // The header subtitle must track the same branch as the title — this view
    // also covers teardown and standards/regions updates, not just setup.
    let subtitle = 'Setup in progress';
    if (hint.includes('disabl')) {
      title = 'Disabling Security Hub…';
      body = 'Tearing down members, aggregator, and per-region Security Hub across every linked region. This can take a few minutes.';
      subtitle = 'Teardown in progress';
    } else if (hint.includes('updating standards')) {
      title = 'Updating compliance standards…';
      body = 'Enabling / disabling the requested standards across every member account and region.';
      subtitle = 'Update in progress';
    } else if (hint.includes('updating regions')) {
      title = 'Updating linking regions…';
      body = 'Enrolling newly-added regions or tearing down removed regions, and updating the finding aggregator.';
      subtitle = 'Update in progress';
    }
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        {header(subtitle)}
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>{title}</Typography>
            <Typography
              sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 2 }}
            >
              {body}
            </Typography>
            <OperationProgress
              percent={config.progress}
              label={config.progressLabel || 'Working…'}
              sx={{ mb: 0 }}
            />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  // Terminal "not usably configured" states — surface the setup wizard so
  // the user can re-run. EnableSecurityHub clears any prior failed/disabled
  // row server-side before creating a new one.
  if (!config || config.status === 'disabled' || config.status === 'failed') {
    return (
      <SecurityHubSetup
        onComplete={refresh}
        priorFailure={config?.status === 'failed' ? config.errorMessage : null}
      />
    );
  }

  return <ComplianceDashboard config={config} onConfigChange={refresh} />;
}
